import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAtlas } from "@/context/AtlasContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { FileText, Plus, Search, Download, ExternalLink, Trash2 } from "lucide-react";
import FileDocumentDialog, { DOC_TYPES, STATUS_OPTIONS } from "@/components/documents/FileDocumentDialog";
import { StatusChip, expiringSoon, downloadDoc } from "@/components/documents/DocumentsPanel";

const inputCls = "h-10 rounded-md border border-slate-200 bg-white px-3 text-sm";

export default function Documents() {
  const { has, user } = useAtlas();
  const canManage = has("files.manage");
  const [docs, setDocs] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [expiring, setExpiring] = useState(false);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(null);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (type) p.set("doc_type", type);
    if (status) p.set("status", status);
    if (expiring) p.set("expiring_days", "30");
    api.get(`/documents?${p.toString()}`).then((r) => setDocs(r.data)).catch((e) => toast.error(apiError(e)));
  }, [q, type, status, expiring]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/organizations").then((r) => setOrgs(r.data || [])).catch(() => setOrgs([])); }, []);

  const openDoc = (id) => api.get(`/documents/${id}`).then((r) => setSel(r.data)).catch((e) => toast.error(apiError(e)));

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="documents-page">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <p className="eyebrow mb-1">Governance</p>
          <h1 className="font-heading text-3xl font-semibold flex items-center gap-2"><FileText className="w-7 h-7" /> Documents & agreements</h1>
          <p className="text-sm text-slate-500 mt-1">File, find and track every agreement across the workspace, with status and renewal reminders.</p>
        </div>
        {canManage && <Button onClick={() => setOpen(true)} className="rounded-full" data-testid="file-document-button"><Plus className="w-4 h-4 mr-1" /> File a document</Button>}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or counterparty" className="pl-9" data-testid="doc-search" />
        </div>
        <select value={type} onChange={(e) => setType(e.target.value)} className={inputCls} data-testid="doc-filter-type">
          <option value="">All types</option>
          {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls} data-testid="doc-filter-status">
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <button onClick={() => setExpiring((v) => !v)} className={`px-3 h-10 rounded-md text-sm border transition-colors ${expiring ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`} data-testid="doc-filter-expiring">Expiring in 30 days</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="documents-table">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b" style={{ borderColor: "var(--line)" }}>
            <th className="px-4 py-2.5">Document</th><th className="px-4 py-2.5">Type</th><th className="px-4 py-2.5">Counterparty</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Effective</th><th className="px-4 py-2.5">Expiry</th><th className="px-4 py-2.5"></th>
          </tr></thead>
          <tbody>
            {docs.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No documents match. File one to get started.</td></tr>}
            {docs.map((d) => (
              <tr key={d.id} onClick={() => openDoc(d.id)} className={`border-b last:border-0 cursor-pointer hover:bg-slate-50 ${expiringSoon(d) ? "bg-amber-50" : ""}`} style={{ borderColor: "var(--line)" }} data-testid={`doc-row-${d.id}`}>
                <td className="px-4 py-3 font-medium text-slate-800">{d.name}{expiringSoon(d) && <span className="ml-2 text-[10px] text-amber-700" data-testid="expiring-soon-badge">expiring soon</span>}</td>
                <td className="px-4 py-3 text-slate-600">{d.doc_type}</td>
                <td className="px-4 py-3 text-slate-600">{d.counterparty_name || <span className="text-slate-300">-</span>}</td>
                <td className="px-4 py-3"><StatusChip status={d.status} /></td>
                <td className="px-4 py-3 text-slate-500 tabular">{d.effective_date || <span className="text-slate-300">-</span>}</td>
                <td className="px-4 py-3 text-slate-500 tabular">{d.expiry_date || <span className="text-slate-300">-</span>}</td>
                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => downloadDoc(d)} className="p-1 text-slate-400 hover:text-slate-800" title="Open" data-testid={`doc-open-${d.id}`}>{d.kind === "link" ? <ExternalLink className="w-4 h-4" /> : <Download className="w-4 h-4" />}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <FileDocumentDialog open={open} onOpenChange={setOpen} onDone={load} orgs={orgs} />
      <DocumentDetail doc={sel} onClose={() => setSel(null)} onChanged={() => { load(); }} canManage={canManage} canDelete={canManage && (sel?.uploaded_by === user?.id || has("admin.access"))} />
    </div>
  );
}

function DocumentDetail({ doc, onClose, onChanged, canManage, canDelete }) {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(doc ? { ...doc } : null); }, [doc]);
  if (!doc || !f) return null;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/documents/${doc.id}`, {
        name: f.name, doc_type: f.doc_type, counterparty_name: f.counterparty_name || "",
        status: f.status_raw, effective_date: f.effective_date || "", expiry_date: f.expiry_date || "",
      });
      toast.success("Document updated");
      setF({ ...data });
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const del = async () => {
    if (!window.confirm(`Remove "${doc.name}"?`)) return;
    try { await api.delete(`/documents/${doc.id}`); toast.success("Document removed"); onClose(); onChanged?.(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Sheet open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="document-detail">
        <SheetHeader><SheetTitle className="font-heading">Document details</SheetTitle></SheetHeader>
        <div className="mt-4 space-y-4">
          <div><Label>Name</Label><Input value={f.name} disabled={!canManage} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="detail-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label><select value={f.doc_type} disabled={!canManage} onChange={(e) => setF({ ...f, doc_type: e.target.value })} className={inputCls + " w-full"} data-testid="detail-type">{DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
            <div><Label>Status</Label><select value={f.status_raw} disabled={!canManage} onChange={(e) => setF({ ...f, status_raw: e.target.value })} className={inputCls + " w-full"} data-testid="detail-status">{STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}</select></div>
          </div>
          <div><Label>Counterparty</Label><Input value={f.counterparty_name || ""} disabled={!canManage} onChange={(e) => setF({ ...f, counterparty_name: e.target.value })} data-testid="detail-counterparty" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Effective date</Label><Input type="date" value={f.effective_date || ""} disabled={!canManage} onChange={(e) => setF({ ...f, effective_date: e.target.value })} data-testid="detail-effective" /></div>
            <div><Label>Expiry / renewal</Label><Input type="date" value={f.expiry_date || ""} disabled={!canManage} onChange={(e) => setF({ ...f, expiry_date: e.target.value })} data-testid="detail-expiry" /></div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => downloadDoc(f)} data-testid="detail-open">{f.kind === "link" ? "Open link" : "Download"}</Button>
            {canManage && <Button className="rounded-full" onClick={save} disabled={saving} data-testid="detail-save">{saving ? "Saving..." : "Save changes"}</Button>}
          </div>
          {canDelete && <button onClick={del} className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1.5" data-testid="detail-delete"><Trash2 className="w-4 h-4" /> Remove document</button>}
        </div>
      </SheetContent>
    </Sheet>
  );
}
